import { getBenchmarkProductionMethod } from '../config/productionMethods'
import {
  LiabilityBreakdown,
  CalculateCBAMLiabilityPayload,
  CalculateCBAMLiabilityResponse
} from '../models/CbamLiabilityCalculationModel'

export type BenchmarkSource = 'user-provided' | 'default-available' | 'defaulted-to-zero'

export type CbamBenchmarkByProductionMethod = Record<string, { cbamBenchmark: number }>
export type BenchmarkSourceByProductionMethod = Record<string, BenchmarkSource>

export type CbamBenchmarkStateByProductionMethod = Record<
  string,
  {
    cbamBenchmark: number
    benchmarkSource: BenchmarkSource
    defaultBenchmark: number | null
  }
>

const abatementRatePerYear: { [key: number]: number } = {
  '2026': 0.975,
  '2027': 0.95,
  '2028': 0.9,
  '2029': 0.775,
  '2030': 0.515,
  '2031': 0.39,
  '2032': 0.265,
  '2033': 0.14,
  '2034': 0
}

const createDefaultBreakdown = (): LiabilityBreakdown => ({
  tonnes: {
    total: 0,
    totalWithIndirectEmissions: 0,
    directEmissions: 0,
    indirectEmissions: 0,
    discount: 0
  },
  money: {
    total: 0,
    totalWithIndirectEmissions: 0,
    directEmissions: 0,
    indirectEmissions: 0,
    discount: 0
  }
})

const yearsToShowDifferentLiabilitiesFor = Object.keys(abatementRatePerYear)

export const calculateCbamLiability = (
  payload: CalculateCBAMLiabilityPayload
): CalculateCBAMLiabilityResponse => {
  const {
    year,
    netMassInTonnes = 0,
    specificIndirectEmission,
    specificDirectEmission,
    cbamBenchmark,
    weeklyAveragePrice,
    customWeeklyAveragePrice,
    productionMethodCode
  } = payload

  const {
    cbamBenchmark: defaultCbamBenchmark,
    productionMethodFound,
    benchMarkFound
  } = getBenchmarkProductionMethod(productionMethodCode)

  if (!productionMethodFound) {
    throw new Error(`Production method ${productionMethodCode} not found`)
  }

  // Determine the benchmark source and value
  let decisiveBenchmark: number
  let benchmarkSource: BenchmarkSource

  if (cbamBenchmark !== undefined) {
    // User explicitly provided a benchmark value
    decisiveBenchmark = cbamBenchmark
    benchmarkSource = 'user-provided'
  } else if (benchMarkFound) {
    // Use the default benchmark from configuration
    decisiveBenchmark = defaultCbamBenchmark
    benchmarkSource = 'default-available'
  } else {
    // No benchmark available, default to 0
    decisiveBenchmark = 0
    benchmarkSource = 'defaulted-to-zero'
  }

  const priceToUse = customWeeklyAveragePrice || weeklyAveragePrice

  let decisiveYear = year <= 2026 ? 2026 : year
  decisiveYear = decisiveYear >= 2034 ? 2034 : decisiveYear
  const liabilities = Object.entries(abatementRatePerYear).reduce(
    (acc, [year, abatementRate]) => {
      const directCo2 = netMassInTonnes * specificDirectEmission
      const indirectCo2 = netMassInTonnes * (specificIndirectEmission || 0)
      const discountCoefficient = decisiveBenchmark * abatementRate
      const discountCo2 = discountCoefficient * netMassInTonnes

      const directLiability = directCo2 * priceToUse
      const indirectLiability = indirectCo2 * priceToUse
      const discountLiability = discountCo2 * priceToUse
      const totalLiability = Math.max(directLiability - discountLiability, 0)
      const totalLiabilityWithIndirectEmissions = Math.max(
        directLiability + indirectLiability - discountLiability,
        0
      )

      acc[year] = {
        liability: totalLiability,
        breakdown: {
          money: {
            total: totalLiability,
            totalWithIndirectEmissions: totalLiabilityWithIndirectEmissions,
            directEmissions: directLiability,
            indirectEmissions: indirectLiability,
            discount: discountLiability
          },
          tonnes: {
            total: Math.max(directCo2 - discountCo2, 0),
            totalWithIndirectEmissions: Math.max(directCo2 + indirectCo2 - discountCo2, 0),
            directEmissions: directCo2,
            indirectEmissions: indirectCo2,
            discount: discountCo2
          }
        }
      }
      return acc
    },
    {} as Record<string, { liability: number; breakdown: LiabilityBreakdown }>
  )

  return {
    cbamBenchmark: decisiveBenchmark,
    defaultCbamBenchmark: benchMarkFound ? defaultCbamBenchmark : 0,
    productionMethodCode,
    weeklyAveragePrice: Number(priceToUse),
    liabilitiesPerYear: liabilities,
    currentYear: liabilities[decisiveYear],
    specificDirectEmission,
    specificIndirectEmission: specificIndirectEmission || 0,
    benchmarkSource
  }
}

export const calculateMultipleCbamLiabilities = (payload: CalculateCBAMLiabilityPayload[]) => {
  return payload.map(calculateCbamLiability)
}

export const aggregateCbamLiabilities = (liabilities: CalculateCBAMLiabilityResponse[]) => {
  return liabilities.reduce(
    (acc, cbamLiability) => {
      const {
        currentYear: { breakdown },
        specificDirectEmission,
        specificIndirectEmission,
        liabilitiesPerYear
      } = cbamLiability

      function getAggregatedBreakdownForYear(year: number) {
        const annualLiability = liabilitiesPerYear[year]

        return {
          breakdown: {
            tonnes: {
              total:
                acc.liabilitiesPerYear[year].breakdown.tonnes.total +
                annualLiability.breakdown.tonnes.total,
              totalWithIndirectEmissions:
                acc.liabilitiesPerYear[year].breakdown.tonnes.totalWithIndirectEmissions +
                annualLiability.breakdown.tonnes.totalWithIndirectEmissions,
              directEmissions:
                acc.liabilitiesPerYear[year].breakdown.tonnes.directEmissions +
                annualLiability.breakdown.tonnes.directEmissions,
              indirectEmissions:
                acc.liabilitiesPerYear[year].breakdown.tonnes.indirectEmissions +
                annualLiability.breakdown.tonnes.indirectEmissions,
              discount:
                acc.liabilitiesPerYear[year].breakdown.tonnes.discount +
                annualLiability.breakdown.tonnes.discount
            },
            money: {
              total:
                acc.liabilitiesPerYear[year].breakdown.money.total +
                annualLiability.breakdown.money.total,
              totalWithIndirectEmissions:
                acc.liabilitiesPerYear[year].breakdown.money.totalWithIndirectEmissions +
                annualLiability.breakdown.money.totalWithIndirectEmissions,
              directEmissions:
                acc.liabilitiesPerYear[year].breakdown.money.directEmissions +
                annualLiability.breakdown.money.directEmissions,
              indirectEmissions:
                acc.liabilitiesPerYear[year].breakdown.money.indirectEmissions +
                annualLiability.breakdown.money.indirectEmissions,
              discount:
                acc.liabilitiesPerYear[year].breakdown.money.discount +
                annualLiability.breakdown.money.discount
            }
          }
        }
      }

      return {
        ...acc,
        specificDirectEmission: acc.specificDirectEmission + specificDirectEmission,
        specificIndirectEmission: acc.specificIndirectEmission + specificIndirectEmission,
        currentYear: {
          breakdown: {
            tonnes: {
              total: acc.currentYear.breakdown.tonnes.total + breakdown.tonnes.total,
              totalWithIndirectEmissions:
                acc.currentYear.breakdown.tonnes.totalWithIndirectEmissions +
                breakdown.tonnes.totalWithIndirectEmissions,
              directEmissions:
                acc.currentYear.breakdown.tonnes.directEmissions + breakdown.tonnes.directEmissions,
              indirectEmissions:
                acc.currentYear.breakdown.tonnes.indirectEmissions +
                breakdown.tonnes.indirectEmissions,
              discount: acc.currentYear.breakdown.tonnes.discount + breakdown.tonnes.discount
            },
            money: {
              total: acc.currentYear.breakdown.money.total + breakdown.money.total,
              totalWithIndirectEmissions:
                acc.currentYear.breakdown.money.totalWithIndirectEmissions +
                breakdown.money.totalWithIndirectEmissions,
              directEmissions:
                acc.currentYear.breakdown.money.directEmissions + breakdown.money.directEmissions,
              indirectEmissions:
                acc.currentYear.breakdown.money.indirectEmissions +
                breakdown.money.indirectEmissions,
              discount: acc.currentYear.breakdown.money.discount + breakdown.money.discount
            }
          }
        },
        liabilitiesPerYear: {
          ...acc.liabilitiesPerYear,
          ...yearsToShowDifferentLiabilitiesFor.reduce(
            (acc, year) => {
              acc[Number(year)] = getAggregatedBreakdownForYear(Number(year))
              return acc
            },
            {} as Record<number, { breakdown: LiabilityBreakdown }>
          )
        }
      }
    },
    {
      currentYear: { breakdown: createDefaultBreakdown() },
      liabilitiesPerYear: yearsToShowDifferentLiabilitiesFor.reduce(
        (acc, year) => {
          acc[year as string] = { breakdown: createDefaultBreakdown() }
          return acc
        },
        {} as Record<string, { breakdown: LiabilityBreakdown }>
      ),
      specificDirectEmission: 0,
      specificIndirectEmission: 0
    }
  )
}

export const calculateAndAggregateCbamLiabilities = (payload: CalculateCBAMLiabilityPayload[]) => {
  const allLiabilities = calculateMultipleCbamLiabilities(payload)
  return aggregateCbamLiabilities(allLiabilities)
}
